import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import { Agreement } from "@modules/agreements/types";
import { ApiError } from "@shared/types";
import { AgreementFormValues } from "@modules/agreements/validations/agreementSchema";
import { createAgreementService } from "./service";
import { UseCreateAgreementMutationOptions } from "./types";
import { listAgreementsQueryKey } from "../../queries/listAgreements/query";
import requestErrorTreatment from "@shared/utils/requestErrorTreatment";

export function useCreateAgreementMutation(
  options?: UseCreateAgreementMutationOptions
) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();

  return useMutation<Agreement, ApiError, AgreementFormValues>({
    mutationFn: createAgreementService,
    onSuccess: (agreement: Agreement) => {
      queryClient.invalidateQueries({
        queryKey: listAgreementsQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("v1/agreement_admin.success.create"),
      });

      navigate(`/agreement/edit/${agreement.id}`);
    },
    onError: (error: ApiError) => {
      requestErrorTreatment(error, open);
    },
    ...options,
  });
}
